/**
 * Reads a JWT's payload **without verifying the signature**. Verification is
 * the API's job; the UI only needs the claims to know what the token grants
 * and when it goes stale. Never treat the result as proof of anything.
 *
 * Returns null for anything that isn't a well-formed token, so a malformed or
 * opaque token degrades to "no claims" rather than throwing.
 */
export function decodeJwt<T>(token: string): T | null {
  const payload = token.split('.')[1];
  if (!payload) {
    return null;
  }
  try {
    // base64url → base64, then restore the padding atob() requires.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    // Via bytes rather than atob() alone, so non-ASCII claim values survive.
    const bytes = Uint8Array.from(atob(padded), character => character.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as T;
  } catch {
    return null;
  }
}
